import { ReactNode } from 'react'
import styled from 'styled-components'

interface InsightsCardWrapperProps {
  /** True when react-query is showing stale data while a fresh fetch is in flight. */
  isShowingPlaceholder: boolean
  /** Stable test-id base, so each card's refetch indicator is addressable. */
  testId: string
  children: ReactNode
}

// Wraps a card with `aria-busy` + a thin indeterminate progress bar while
// react-query shows placeholder (stale) data. Must NOT clip overflow — the
// Recharts tooltip is absolutely positioned and needs to extend past the card
// edge (NEX-681); the bar rounds its own top corners to stay flush.
const Wrapper = styled.div`
  ${({ theme }) => `
    flex: 1;
    min-width: 0;
    position: relative;
    display: flex;
    border-radius: ${theme.borderRadii.xlarge};
  `}
`

const ProgressBar = styled.div`
  ${({ theme }) => `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    overflow: hidden;
    pointer-events: none;
    z-index: 1;
    border-top-left-radius: ${theme.borderRadii.xlarge};
    border-top-right-radius: ${theme.borderRadii.xlarge};
  `}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    /* b3 = ~70% alpha on the app's primary colour (theme token, not a raw hex). */
    background: ${({ theme }) => `linear-gradient(
      90deg,
      transparent 0%,
      ${theme.colors.primary400}b3 50%,
      transparent 100%
    )`};
    animation: indeterminate 1.2s infinite linear;
  }

  @keyframes indeterminate {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`

export function InsightsCardWrapper({
  isShowingPlaceholder,
  testId,
  children,
}: Readonly<InsightsCardWrapperProps>) {
  return (
    <Wrapper aria-busy={isShowingPlaceholder || undefined}>
      {isShowingPlaceholder && (
        <ProgressBar data-testid={`${testId}-refetching`} />
      )}
      {children}
    </Wrapper>
  )
}
